import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Type, Pencil, Upload, RotateCcw, CheckCircle } from 'lucide-react';

// Handwriting-style fonts available via system + Google Fonts fallback
const SIGNATURE_FONTS = [
  { name: 'Dancing Script', css: "'Dancing Script', cursive" },
  { name: 'Great Vibes', css: "'Great Vibes', cursive" },
  { name: 'Satisfy', css: "'Satisfy', cursive" },
  { name: 'Sacramento', css: "'Sacramento', cursive" },
];

// Load Google Fonts for typed signatures
const GOOGLE_FONTS_URL =
  'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&family=Great+Vibes&family=Satisfy&family=Sacramento&display=swap';

let fontsLoaded = false;
function ensureFontsLoaded() {
  if (fontsLoaded) return;
  fontsLoaded = true;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = GOOGLE_FONTS_URL;
  document.head.appendChild(link);
}

interface SignaturePadProps {
  signerName?: string;
  onSignatureChange: (signatureDataUrl: string | null) => void;
  savedSignatureImage?: string | null;
}

export default function SignaturePad({ signerName = '', onSignatureChange, savedSignatureImage = null }: SignaturePadProps) {
  const { t } = useTranslation(['documents']);
  const hasSaved = Boolean(savedSignatureImage);
  const [activeTab, setActiveTab] = useState(hasSaved ? 'saved' : 'type');

  // --- Type tab state ---
  const [typedName, setTypedName] = useState(signerName);
  const [selectedFont, setSelectedFont] = useState(0);
  const typeCanvasRef = useRef<HTMLCanvasElement>(null);

  // --- Draw tab state ---
  const drawCanvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  // --- Upload tab state ---
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    ensureFontsLoaded();
  }, []);

  // Emit saved signature immediately when starting on saved tab
  useEffect(() => {
    if (hasSaved && activeTab === 'saved') {
      onSignatureChange(savedSignatureImage);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render typed signature to canvas and emit
  const renderTypedSignature = useCallback(() => {
    const canvas = typeCanvasRef.current;
    if (!canvas || !typedName.trim()) {
      onSignatureChange(null);
      return;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const w = 340;
    const h = 100;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    ctx.scale(dpr, dpr);

    ctx.clearRect(0, 0, w, h);

    const font = SIGNATURE_FONTS[selectedFont];
    ctx.font = `36px ${font.css}`;
    ctx.fillStyle = '#1a1a2e';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.fillText(typedName.trim(), w / 2, h / 2);

    onSignatureChange(canvas.toDataURL('image/png'));
  }, [typedName, selectedFont, onSignatureChange]);

  useEffect(() => {
    if (activeTab === 'type') {
      // Small delay to let fonts load
      const timeout = setTimeout(renderTypedSignature, 150);
      return () => clearTimeout(timeout);
    }
  }, [activeTab, renderTypedSignature]);

  // --- Draw canvas logic ---
  const initDrawCanvas = useCallback(() => {
    const canvas = drawCanvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, rect.width, rect.height);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = '#1a1a2e';
  }, []);

  useEffect(() => {
    if (activeTab === 'draw') {
      // Re-init when tab becomes active
      setTimeout(initDrawCanvas, 50);
      setHasDrawn(false);
      onSignatureChange(null);
    }
  }, [activeTab, initDrawCanvas, onSignatureChange]);

  const getCanvasPoint = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = drawCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const canvas = drawCanvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;
    const { x, y } = getCanvasPoint(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = drawCanvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;
    const { x, y } = getCanvasPoint(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    // Emit signature after each stroke
    const canvas = drawCanvasRef.current;
    if (canvas) {
      onSignatureChange(canvas.toDataURL('image/png'));
    }
  };

  const clearDrawCanvas = () => {
    initDrawCanvas();
    setHasDrawn(false);
    onSignatureChange(null);
  };

  // --- Upload logic ---
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) return;
    if (file.size > 5 * 1024 * 1024) return; // 5MB limit

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setUploadedImage(dataUrl);
      onSignatureChange(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const clearUpload = () => {
    setUploadedImage(null);
    onSignatureChange(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    // Re-emit saved signature when switching back to saved tab, clear otherwise
    if (tab === 'saved' && savedSignatureImage) {
      onSignatureChange(savedSignatureImage);
    } else {
      onSignatureChange(null);
    }
  };

  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium">{t('documents:signature_label')}</Label>
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className={`grid w-full ${hasSaved ? 'grid-cols-4' : 'grid-cols-3'}`}>
          {hasSaved && (
            <TabsTrigger value="saved" className="text-xs gap-1.5">
              <CheckCircle className="h-3.5 w-3.5" />
              {t('documents:signature_tab_saved')}
            </TabsTrigger>
          )}
          <TabsTrigger value="type" className="text-xs gap-1.5">
            <Type className="h-3.5 w-3.5" />
            {t('documents:signature_tab_type')}
          </TabsTrigger>
          <TabsTrigger value="draw" className="text-xs gap-1.5">
            <Pencil className="h-3.5 w-3.5" />
            {t('documents:signature_tab_draw')}
          </TabsTrigger>
          <TabsTrigger value="upload" className="text-xs gap-1.5">
            <Upload className="h-3.5 w-3.5" />
            {t('documents:signature_tab_upload')}
          </TabsTrigger>
        </TabsList>

        {/* SAVED TAB */}
        {hasSaved && (
          <TabsContent value="saved" className="space-y-2">
            <p className="text-xs text-muted-foreground">{t('documents:signature_saved_hint')}</p>
            <div className="border rounded-md bg-white p-2 flex items-center justify-center">
              <img
                src={savedSignatureImage!}
                alt="Saved signature"
                className="max-h-[100px] max-w-full object-contain"
              />
            </div>
          </TabsContent>
        )}

        {/* TYPE TAB */}
        <TabsContent value="type" className="space-y-3">
          <Input
            placeholder={t('documents:signature_type_placeholder')}
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            className="text-sm"
          />
          {typedName.trim() && (
            <>
              <div className="flex gap-2 flex-wrap">
                {SIGNATURE_FONTS.map((font, i) => (
                  <button
                    key={font.name}
                    type="button"
                    onClick={() => setSelectedFont(i)}
                    className={`px-3 py-1.5 rounded-md border text-sm transition-colors ${
                      selectedFont === i
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border hover:border-primary/50'
                    }`}
                    style={{ fontFamily: font.css }}
                  >
                    {typedName.trim().slice(0, 12)}
                  </button>
                ))}
              </div>
              <div className="border rounded-md bg-white flex items-center justify-center overflow-hidden">
                <canvas
                  ref={typeCanvasRef}
                  style={{ width: 340, height: 100 }}
                />
              </div>
            </>
          )}
        </TabsContent>

        {/* DRAW TAB */}
        <TabsContent value="draw" className="space-y-2">
          <p className="text-xs text-muted-foreground">{t('documents:signature_draw_hint')}</p>
          <div className="relative border rounded-md bg-white overflow-hidden">
            <canvas
              ref={drawCanvasRef}
              className="w-full cursor-crosshair touch-none"
              style={{ height: 120 }}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
            {hasDrawn && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute top-1 right-1 h-7 w-7 p-0"
                onClick={clearDrawCanvas}
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </TabsContent>

        {/* UPLOAD TAB */}
        <TabsContent value="upload" className="space-y-2">
          {uploadedImage ? (
            <div className="relative border rounded-md bg-white p-2 flex items-center justify-center">
              <img
                src={uploadedImage}
                alt="Uploaded signature"
                className="max-h-[100px] max-w-full object-contain"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute top-1 right-1 h-7 w-7 p-0"
                onClick={clearUpload}
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : (
            <div
              className="border-2 border-dashed rounded-md p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">{t('documents:signature_upload_hint')}</p>
              <p className="text-xs text-muted-foreground mt-1">{t('documents:signature_upload_formats')}</p>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={handleFileUpload}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
